import { InfiniteList } from '@/components/common/InfiniteList';
import { useQuery } from '@apollo/client';
import { TransactionEntriesDocument } from '@common/graphql/generated';
import {
    useFinancialEntityMap,
    useTransactionEntries,
    useTransactionEntryCategories,
} from '@common/react/cashflow';
import { FC } from 'react';
import TransactionEntryItem from './TransactionEntryItem';

export interface TransactionEntriesListProps {
    financialEntityId?: string;
    startDate?: string;
    endDate?: string;
    hoverColor?: string;
}

const ITEMS_PER_PAGE = 5;

const TransactionEntriesList: FC<TransactionEntriesListProps> = ({
    financialEntityId,
    startDate,
    endDate,
    hoverColor,
}) => {
    const { transactionEntries, fetchMore, loading, endCursor, hasNextPage } =
        useTransactionEntriesForList({
            financialEntityId,
            startDate,
            endDate,
            itemsPerPage: ITEMS_PER_PAGE,
        });

    return (
        <InfiniteList
            items={transactionEntries}
            itemsPerPage={ITEMS_PER_PAGE}
            renderItem={(item) => {
                return (
                    <TransactionEntryItem
                        key={item.id}
                        transactionEntry={item}
                        hoverColor={hoverColor}
                    />
                );
            }}
            emptyState="No transactions yet"
            queryResult={{ fetchMore, loading }}
            endCursor={endCursor}
            hasNextPage={hasNextPage}
        />
    );
};

const useTransactionEntriesForList = (variables: {
    financialEntityId?: string;
    startDate?: string;
    endDate?: string;
    itemsPerPage: number;
}) => {
    const result = useQuery(TransactionEntriesDocument, { variables });
    const payload = result.data?.transactionEntriesConnection;

    const {
        transactionEntryCategoriesMapById,
        transactionEntryCategoriesLoading,
    } = useTransactionEntryCategories();

    const { financialEntityMap, financialEntityMapLoading } =
        useFinancialEntityMap();

    const transactionEntries = useTransactionEntries({
        transactionEntries: payload?.edges.map((e) => e.node) ?? [],
        financialEntityMap,
        transactionEntryCategoriesMapById,
    });

    return {
        transactionEntries,
        endCursor: payload?.pageInfo.endCursor,
        hasNextPage: !!payload?.pageInfo.hasNextPage,
        loading:
            result.loading ||
            transactionEntryCategoriesLoading ||
            financialEntityMapLoading,
        fetchMore: result.fetchMore,
    };
};

export default TransactionEntriesList;
